import { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { Link } from 'react-router-dom';
import { Icon } from '@iconify/react';

import { MagneticLink } from '../anim/MagneticLink.jsx';
import { fetchSampleTenders } from '../lib/api.js';

/**
 * HeroLedger  -  terminal-feel live tender ticker.
 *
 * Left rail: editorial headline, signal-orange accent. Right rail: a
 * monospace ticker pinned to real samples from /v1/sample/notices.
 * Polls every POLL_MS; only when genuinely new notice_ids show up do
 * we prepend rows + animate the fly-in. No fake churn — silence on
 * the wire = silence in the ticker, which is the honest signal.
 *
 * Polish layer: live UTC clock in the eyebrow, subtle cursor parallax
 * on the ticker (≤ 4px translate, eased). Pitched at finance/
 * procurement leads; developer pitch lives in the curl block beneath
 * the headline.
 */
const POLL_MS = 30_000;
const TICKER_PORTALS = ['ch-simap', 'gb-fts', 'us-sam', 'ua-prozorro', 'gb-contracts-finder'];
const MAX_ROWS = 8;

export function HeroLedger() {
  const root = useRef(null);
  const tickerWrap = useRef(null);
  const [rows, setRows] = useState(seedRows());
  const [count, setCount] = useState(null);
  const [now, setNow] = useState(() => new Date());
  // Track every notice_id we've already shown so re-polls don't double-count.
  const seenIds = useRef(new Set());

  // Initial hydrate + recurring poll. Only prepends rows that are
  // actually new (notice_id not in seenIds).
  useEffect(() => {
    let cancelled = false;

    async function poll() {
      const batches = await Promise.all(
        TICKER_PORTALS.map((p) => fetchSampleTenders({ limit: 6, portal: p }).catch(() => [])),
      );
      if (cancelled) return;

      // Flatten + sort by fetched_at DESC so the absolute newest rows
      // across all portals float to the top. (Round-robin merge meant
      // a 5-day-old SIMAP record could sit above a 2-min-old GB-FTS
      // row, which made "8 MOST RECENT" lie.)
      const flat = batches.flat();
      flat.sort((a, b) => fetchedAtMs(b) - fetchedAtMs(a));
      const usable = flat.filter((s) => extractMultilang(s.title));
      setCount(usable.length);
      if (usable.length === 0) return;

      const mapped = usable.map((s, i) => liveToRow(s, i));

      // First load: seed the ticker with the freshest MAX_ROWS rows.
      if (seenIds.current.size === 0) {
        for (const row of mapped.slice(0, MAX_ROWS)) seenIds.current.add(row.id);
        setRows(mapped.slice(0, MAX_ROWS));
        return;
      }

      // Subsequent polls: only prepend genuinely new rows.
      const fresh = mapped.filter((row) => !seenIds.current.has(row.id));
      if (fresh.length === 0) return;
      for (const row of fresh) seenIds.current.add(row.id);
      setRows((prev) => [...fresh, ...prev].slice(0, MAX_ROWS));
    }

    poll();
    const id = setInterval(poll, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  // intro reveal
  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: 'expo.out' } });
      tl.from('.eyebrow', { autoAlpha: 0, y: 8, duration: 0.6 }, 0)
        .from('.h1-line > span', { yPercent: 102, stagger: 0.11, duration: 1.0 }, 0.05)
        .from('.lede', { autoAlpha: 0, y: 12, duration: 0.7 }, 0.55)
        .from('.cta-row', { autoAlpha: 0, y: 10, duration: 0.6 }, 0.7)
        .from('.proof-row', { autoAlpha: 0, y: 8, duration: 0.5 }, 0.85)
        .from('.hero-curl', { autoAlpha: 0, y: 12, duration: 0.6 }, 0.95)
        .from('.ticker', { autoAlpha: 0, x: 24, duration: 0.9 }, 0.25)
        .from('.ticker-row', { autoAlpha: 0, x: 16, duration: 0.5, stagger: 0.06 }, 0.45);
    }, root);
    return () => ctx.revert();
  }, []);

  // When rows change because a poll surfaced fresh notices, animate the
  // top row(s) in. No animation on initial mount (intro reveal handles
  // that) and none when the row set is unchanged.
  const prevTopId = useRef(null);
  useEffect(() => {
    const topRow = root.current?.querySelector('.ticker-row:first-child');
    if (!topRow) return;
    const topId = rows[0]?.id;
    if (!topId || topId === prevTopId.current) return;
    prevTopId.current = topId;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    gsap.fromTo(
      topRow,
      { opacity: 0, y: -8, backgroundColor: 'var(--accent-soft)' },
      {
        opacity: 1,
        y: 0,
        backgroundColor: 'transparent',
        duration: 0.55,
        ease: 'expo.out',
      },
    );
  }, [rows]);

  // 1Hz clock for the eyebrow + "received +Δs ago" labels
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  // cursor parallax on the ticker  -  ≤4 px translate, long ease
  useEffect(() => {
    const wrap = tickerWrap.current;
    if (!wrap) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return;

    let raf = 0;
    const target = { x: 0, y: 0 };
    const cur = { x: 0, y: 0 };
    function onMove(e) {
      const r = wrap.getBoundingClientRect();
      const dx = (e.clientX - (r.left + r.width / 2)) / r.width;
      const dy = (e.clientY - (r.top + r.height / 2)) / r.height;
      target.x = Math.max(-1, Math.min(1, dx)) * 4;
      target.y = Math.max(-1, Math.min(1, dy)) * 3;
    }
    function onLeave() {
      target.x = 0;
      target.y = 0;
    }
    function tick() {
      cur.x += (target.x - cur.x) * 0.06;
      cur.y += (target.y - cur.y) * 0.06;
      wrap.style.transform = `translate3d(${cur.x.toFixed(2)}px, ${cur.y.toFixed(2)}px, 0)`;
      raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);
    window.addEventListener('pointermove', onMove);
    wrap.addEventListener('pointerleave', onLeave);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('pointermove', onMove);
      wrap.removeEventListener('pointerleave', onLeave);
    };
  }, []);

  return (
    <section ref={root} className="hero hero-ledger">
      <div className="hero-grid">
        {/* LEFT: pitch */}
        <div className="hero-left">
          <p className="eyebrow mono">
            <span className="dot" />
            live · {now.toISOString().slice(11, 19)} UTC · {count ?? ' - '} samples cached
          </p>
          <h1 className="hero-headline mono">
            <span className="h1-line">
              <span>Every public tender.</span>
            </span>
            <span className="h1-line">
              <span>One JSON feed.</span>
            </span>
            <span className="h1-line">
              <span className="accent">Minute-level freshness.</span>
            </span>
          </h1>
          <p className="lede">
            Public-procurement portals across the EU, UK, US, Switzerland and
            beyond, normalised into one JSON feed. The intelligence other
            aggregation services charge <em>$15,000 / yr</em> for in nightly
            bulk  -  streamed to your institution in real time for <em>$999
            / month</em>. One key, every department member.
          </p>

          <div className="cta-row">
            <MagneticLink href="/signup" variant="primary">
              Get an institution key
            </MagneticLink>
            <MagneticLink href="/sample" variant="secondary">
              See live samples
            </MagneticLink>
          </div>

          <div className="proof-row mono">
            <span>no card to view samples</span>
            <span className="sep">·</span>
            <span>cancel anytime</span>
            <span className="sep">·</span>
            <span>regulator-grade sources only</span>
          </div>

          <pre className="hero-curl">
{`# Stream new tenders in real time
curl -N -H "X-Api-Key: \${OMNI_KEY}" \\
  https://tender-api.com/v1/eu-tenders/stream`}
          </pre>
        </div>

        {/* RIGHT: live ticker */}
        <div className="ticker" ref={tickerWrap}>
          <div className="ticker-head mono">
            <span className="ticker-title">/v1/eu-tenders/stream</span>
            <span className="ticker-status">
              <span className="led" /> LIVE
            </span>
          </div>
          <div className="ticker-cols mono">
            <span>iso</span>
            <span>cy</span>
            <span>kind</span>
            <span>buyer</span>
            <span>title</span>
            <span>value</span>
          </div>
          <div className="ticker-body mono">
            {rows.map((r) => (
              <div className="ticker-row" key={r.id}>
                <span className="c-ts">{r.ts}</span>
                <span className="c-cy">
                  <Icon
                    icon={`flag:${(r.country || '').toLowerCase()}-4x3`}
                    className="cy-flag"
                    aria-hidden="true"
                  />
                  <span className="cy-code">{r.country}</span>
                </span>
                <span className={`c-kind kind-${r.kind || 'unknown'}`}>{kindLabel(r.kind)}</span>
                <span className="c-buyer">{r.buyer}</span>
                <span className="c-title">{r.title}</span>
                <span className="c-val">{r.value}</span>
              </div>
            ))}
          </div>
          <div className="ticker-foot mono">
            <span>{rows.length} most recent</span>
            <Link to="/sample" className="ticker-link">
              full schema →
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/* =================================================================== */

function truncate(s, n) {
  if (!s) return '';
  s = String(s);
  return s.length > n ? s.slice(0, n - 1) + '...' : s;
}
const CURRENCY_SYMBOL = { EUR: '€', USD: '$', GBP: '£', CHF: 'CHF ', UAH: '₴' };
function formatValue(v, currency) {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return '...';
  let body;
  if (n >= 1e9) body = `${(n / 1e9).toFixed(1)}B`;
  else if (n >= 1e6) body = `${(n / 1e6).toFixed(1)}M`;
  else if (n >= 1e3) body = `${(n / 1e3).toFixed(0)}k`;
  else body = String(n);
  const sym = currency ? CURRENCY_SYMBOL[currency] || `${currency} ` : '';
  return `${sym}${body}`;
}
// extractMultilang resolves an ADR 0007 multilang object {primary_lang, values}
// or returns the input unchanged if already a string. Prefers English when
// available since the marketing ticker is English-language. Defends every
// notice renderer from the React #31 "object as child" crash.
function extractMultilang(t) {
  if (t == null) return '';
  if (typeof t === 'string') return t;
  if (typeof t === 'object' && t.values) {
    if (t.values.en) return t.values.en;
    const lang = t.primary_lang || 'en';
    return t.values[lang] || Object.values(t.values).find(Boolean) || '';
  }
  return '';
}
function liveToRow(s, i) {
  const amount = typeof s.value === 'number' ? s.value : s.value?.amount;
  const currency = typeof s.value === 'object' ? s.value?.currency : undefined;
  return {
    id: s.notice_id || s.id || `live-${i}`,
    country: (s.country || 'EU').toUpperCase(),
    kind: s.notice_kind || 'contract_notice',
    buyer: truncate(s.buyer_name || s.buyer?.name || 'Unknown buyer', 26),
    title: truncate(extractMultilang(s.title) || 'Untitled tender', 42),
    value: amount ? formatValue(amount, currency) : '...',
    ts: pickTs(s),
    received: Date.now() - i * 18_000,
  };
}
// Compact label per ADR 0007 notice_kind enum.
const KIND_LABEL = {
  prior_information: 'PIN',
  contract_notice: 'NEW',
  award: 'AWARD',
  modification: 'MOD',
  cancellation: 'CXL',
  corrigendum: 'CORR',
};
function kindLabel(kind) {
  return KIND_LABEL[kind] || 'NEW';
}
// pickTs returns the HH:MM:SS the ticker shows in the ISO column.
// Prefer fetched_at (the moment our worker ingested the notice) over
// published_at, because some portals only publish a date (00:00:00
// UTC) which made the ticker look broken. fetched_at is always a
// real wall-clock moment.
function pickTs(s) {
  const v = s.fetched_at || s.published_at || new Date().toISOString();
  try {
    const d = new Date(v);
    return d.toISOString().slice(11, 19);
  } catch (_) {
    return new Date().toISOString().slice(11, 19);
  }
}
// fetchedAtMs returns the millisecond epoch of when our pipeline
// ingested a notice — used for sorting the merged sample so the most
// recently-fetched rows float to the top of the ticker regardless of
// source portal.
function fetchedAtMs(s) {
  const v = s.fetched_at || s.published_at;
  if (!v) return 0;
  const t = Date.parse(v);
  return Number.isFinite(t) ? t : 0;
}

const SEED_BUYERS = [
  ['DE', 'contract_notice', 'Bundesamt für Bauwesen', 'Sanierung Bürogebäude Berlin-Mitte', '€2.4M'],
  ['FR', 'award', 'Région Île-de-France', 'Maintenance flotte autobus 2026-29', '€18.5M'],
  ['IT', 'contract_notice', 'ANAS S.p.A.', 'Manutenzione tratti autostrada A14', '€42.0M'],
  ['ES', 'corrigendum', 'Ayuntamiento Madrid', 'Servicios limpieza distrito centro', '€5.1M'],
  ['NL', 'contract_notice', 'Rijkswaterstaat', 'IT-beheerdiensten 2026-2030', '€12.0M'],
  ['CH', 'award', 'SBB AG', 'Beschaffung Niederflur-Triebzüge', '€220M'],
  ['IE', 'contract_notice', 'Dept. of Education', 'School ICT framework procurement', '€8.4M'],
  ['SE', 'cancellation', 'Trafikverket', 'Underhåll järnväg E-län 2026', '€15.6M'],
  ['PL', 'prior_information', 'Ministerstwo Obrony', 'Modernizacja systemów IT', '€44.2M'],
];
function seedRows() {
  const now = Date.now();
  return SEED_BUYERS.map((b, i) => ({
    id: `seed-${i}`,
    country: b[0],
    kind: b[1],
    buyer: b[2],
    title: b[3],
    value: b[4],
    ts: new Date(now - i * 11_000).toISOString().slice(11, 19),
    received: now - i * 11_000,
  }));
}
function synthRow(prevId) {
  const idx = Math.floor(Math.random() * SEED_BUYERS.length);
  const b = SEED_BUYERS[idx];
  const now = Date.now();
  return {
    id: `${now}-${prevId || 'r'}`,
    country: b[0],
    kind: b[1],
    buyer: b[2],
    title: b[3],
    value: b[4],
    ts: new Date(now).toISOString().slice(11, 19),
    received: now,
  };
}
